class AtlHeader extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.mobile = this.querySelector('.atl-header__mobile');
    this.trigger = this.mobile.querySelector('summary');
    this.drawer = this.querySelector('.atl-header__drawer');
    this.panels = [...this.querySelectorAll('.atl-header__menu')];
    this.scrollHost = document.querySelector('.page-wrapper');
    this.syncScroll = () => this.toggleAttribute('data-scrolled', Math.max(window.scrollY, this.scrollHost?.scrollTop || 0) > 8);
    this.syncScroll();
    window.addEventListener('scroll', this.syncScroll, { ...options, passive: true });
    this.scrollHost?.addEventListener('scroll', this.syncScroll, { ...options, passive: true });
    this.panels.forEach(panel => {
      panel.addEventListener('toggle', () => {
        panel.querySelector('summary').setAttribute('aria-expanded', String(panel.open));
        if (panel.open) this.panels.forEach(other => { if (other !== panel) other.open = false; });
      }, options);
    });
    document.addEventListener('click', event => {
      if (!this.contains(event.target)) this.panels.forEach(panel => { panel.open = false; });
    }, options);
    this.addEventListener('focusout', event => {
      // activeElement can still be body while the browser moves focus from
      // a summary to its link. Keep that panel open through the click.
      this.panels.forEach(panel => {
        if (panel.contains(event.target) && !panel.contains(event.relatedTarget)) panel.open = false;
      });
    }, options);
    this.mobile.addEventListener('toggle', () => this.syncMobile(), options);
    this.querySelector('[data-atl-close]').addEventListener('click', () => this.closeMobile(), options);
    this.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        if (this.mobile.open) { event.preventDefault(); this.closeMobile(); }
        this.panels.forEach(panel => { if (panel.open) { panel.open = false; panel.querySelector('summary').focus(); } });
      }
      if (event.key === 'Tab' && this.mobile.open) {
        const focusable = [...this.mobile.querySelectorAll('summary,a[href],button')].filter(el => el.getClientRects().length);
        const first = focusable[0]; const last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    }, options);
    this.desktopQuery = matchMedia('(min-width: 990px)');
    this.desktopQuery.addEventListener('change', () => { if (this.desktopQuery.matches) this.closeMobile(false); }, options);
    this.querySelector('[data-atl-search]').addEventListener('click', event => {
      const modal = document.getElementById('search-modal');
      if (typeof modal?.showDialog === 'function') { event.preventDefault(); this.closeMobile(false); modal.showDialog(); }
    }, options);
    this.querySelector('[data-atl-cart]').addEventListener('click', event => {
      const cart = document.getElementById('cart-drawer');
      if (typeof cart?.open === 'function') { event.preventDefault(); this.closeMobile(false); cart.open(); }
    }, options);
    this.bindCartEvents(options);
  }
  async bindCartEvents(options) {
    try {
      const { StandardEvents } = await import('@shopify/events');
      if (this.abort.signal.aborted) return;
      document.addEventListener(StandardEvents.cartLinesUpdate, async event => {
        try {
          const result = await event.promise;
          if (result?.cart?.totalQuantity === undefined) return;
          const count = this.querySelector('[data-atl-count]');
          count.textContent = result.cart.totalQuantity;
          count.hidden = result.cart.totalQuantity === 0;
        } catch { }
      }, options);
    } catch { }
  }
  syncMobile() {
    this.trigger.setAttribute('aria-expanded', String(this.mobile.open));
    if (this.mobile.open) {
      this.inertTargets = [...document.querySelectorAll('#MainContent, .page-wrapper > footer')].filter(el => !el.inert);
      this.inertTargets.forEach(el => { el.inert = true; });
      this.previousOverflow = this.scrollHost.style.overflow;
      this.scrollHost.style.overflow = 'hidden';
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.querySelector('[data-atl-close]').focus();
    } else this.releaseMobile();
  }
  releaseMobile() {
    (this.inertTargets || []).forEach(el => { el.inert = false; });
    this.inertTargets = [];
    if (this.previousOverflow !== undefined) {
      this.scrollHost.style.overflow = this.previousOverflow;
      document.body.style.overflow = this.previousBodyOverflow;
      this.previousOverflow = undefined;
    }
  }
  closeMobile(restoreFocus = true) {
    if (!this.mobile.open) return;
    this.mobile.open = false;
    this.releaseMobile();
    if (restoreFocus) this.trigger.focus();
  }
  disconnectedCallback() { this.releaseMobile(); this.abort?.abort(); }
}
if (!customElements.get('atl-header')) customElements.define('atl-header', AtlHeader);
